import {
  GoAButton,
  GoAButtonGroup,
  GoAIconButton,
  GoAInput,
  GoAInputText,
  GoASpacer,
} from '@abgov/react-components';

import styles from './time-picker.module.css';

import React, { useEffect, useRef, useState } from 'react';
import { set } from 'lodash';

interface TimePickerProps {
  hours: number;
  minutes: number;
  period: string;
  disabled?: boolean;
  error?: boolean;
  onChange?: (timeString: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  setCustomError?: (message: string) => void;
}

export const TimePickerComponent = ({
  hours,
  minutes,
  period,
  error,
  disabled,
  onChange,
  onOpen,
  onClose,
  setCustomError,
}: TimePickerProps) => {
  const amPmError = 'Choose AM or PM in order to continue';

  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [pmSelected, setPmSelected] = useState(false);
  const amSelectedRef = useRef<boolean | undefined>(undefined);
  const [amSelected, setAmSelected] = useState(false);
  const pmSelectedRef = useRef<boolean | undefined>(undefined);

  const [hoursLabel, setHoursLabel] = useState(
    hours ? hours.toString().padStart(2, '0') : '00',
  );
  const [minutesLabel, setMinutesLabel] = useState(
    minutes ? minutes.toString().padStart(2, '0') : '00',
  );
  const [formattedTimeLabel, setFormattedTimeLabel] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDateTimePicker(false);
        validateAmPm();

        if (onClose) onClose();
      }
    };

    // Add event listener for keydown
    document.addEventListener('keydown', handleEscKey);

    // Clean up the event listener when the component is unmounted
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  useEffect(() => {
    if (amSelected) {
      period = 'AM';
    } else if (pmSelected) {
      period = 'PM';
    }

    hours = parseInt(hoursLabel, 10);
    minutes = parseInt(minutesLabel, 10);

    if (hoursLabel != '00' && (pmSelected || amSelected)) {
      setFormattedTimeLabel(`${hoursLabel}:${minutesLabel} ${period}`);

      if (
        amSelectedRef.current != undefined &&
        amSelectedRef.current != amSelected
      ) {
        amSelectedRef.current = amSelected;

        handleClose();
      }
      if (pmSelectedRef != undefined && pmSelectedRef.current != pmSelected) {
        pmSelectedRef.current = pmSelected;

        handleClose();
      }
    } else {
      setFormattedTimeLabel('');
    }
  }, [hoursLabel, minutesLabel, amSelected, pmSelected]);

  useEffect(() => {
    if (onChange) onChange(formattedTimeLabel);
  }, [formattedTimeLabel]);

  useEffect(() => {
    if (showDateTimePicker) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }

    setHoursLabel(hours.toString().padStart(2, '0'));
    setMinutesLabel(minutes.toString().padStart(2, '0'));

    if (period) {
      if (period === 'AM') {
        setAmSelected(true);
        setPmSelected(false);
      } else if (period === 'PM') {
        setAmSelected(false);
        setPmSelected(true);
      } else {
        setAmSelected(false);
        setPmSelected(false);
      }
    } else {
      setAmSelected(false);
      setPmSelected(false);
    }
  }, [hours, minutes, period]);

  const handleClose = () => {
    setShowDateTimePicker(false);

    if (onClose) onClose();
  };
  const validateAmPm = () => {
    if (setCustomError && hoursLabel !== '00' && !pmSelected && !amSelected) {
      setCustomError(amPmError);
    }
  };

  const handleChange = (
    value: string,
    setValue: (value: string) => void,
    max: number,
    nextRef?: React.RefObject<HTMLInputElement>,
  ) => {
    let inputValue = value.replace(/\D/g, '');

    if (inputValue.length === 1) {
      inputValue = '0' + inputValue;
    } else if (inputValue.length === 0) {
      inputValue = '00';
    } else if (inputValue.length > 2) {
      inputValue = inputValue.slice(-2);
    }

    const numberValue = parseInt(inputValue);
    if (inputValue === '' || (numberValue >= 0 && numberValue <= max)) {
      setValue(inputValue);
    }
  };

  const handleFocus = (value: string, setValue: (value: string) => void) => {
    if (value === '00') {
      setValue('');
    }
  };

  const handleBlur = (value: string, setValue: (value: string) => void) => {
    if (value === '') {
      setValue('00');
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
      <div tabIndex={1}>
        <GoAInputText
          name="timepicker"
          value={formattedTimeLabel}
          onChange={() => {}}
          onFocus={() => {
            setShowDateTimePicker(true);
            if (onOpen) onOpen();
          }}
          trailingIcon="time"
          disabled={disabled}
          error={error}
        ></GoAInputText>
      </div>
      <div
        className={styles.modalcontainer}
        style={{
          display: showDateTimePicker ? 'flex' : 'none',
        }}
      >
        <div className={styles.timepickermaincontainer}>
          <div className={styles.closebutton}>
            <GoAIconButton
              icon="close-circle"
              onClick={() => {
                validateAmPm();

                handleClose();
              }}
            ></GoAIconButton>
          </div>
          <div className={styles.timepickercontainer}>
            <GoASpacer hSpacing="l" />
            <div className={styles.timecontainer}>
              <GoAIconButton
                icon="caret-up"
                onClick={() => {
                  if (hoursLabel === '12') {
                    setHoursLabel('00');
                  } else {
                    setHoursLabel(
                      (parseInt(hoursLabel) + 1).toString().padStart(2, '0'),
                    );
                  }
                }}
              ></GoAIconButton>

              <input
                className={styles.timebox}
                type="text"
                value={hoursLabel}
                onChange={(e) => {
                  handleChange(e.target.value, setHoursLabel, 12);
                }}
                onFocus={(e) => {
                  handleFocus(e.target.value, setHoursLabel);
                }}
                onBlur={(e) => {
                  handleBlur(e.target.value, setHoursLabel);
                }}
                tabIndex={2}
                ref={inputRef}
              ></input>

              <GoAIconButton
                icon="caret-down"
                onClick={() => {
                  if (hoursLabel === '00') {
                    setHoursLabel('12');
                  } else {
                    setHoursLabel(
                      (parseInt(hoursLabel) - 1).toString().padStart(2, '0'),
                    );
                  }
                }}
              ></GoAIconButton>

              <div className={styles.timecontainertext}>Hour</div>
            </div>
            <GoASpacer hSpacing="xs" />
            <div>
              <div className={styles.timeseparator}>:</div>
              <div style={{ minHeight: '20px' }}></div>
            </div>
            <GoASpacer hSpacing="xs" />
            <div className={styles.timecontainer}>
              <GoAIconButton
                icon="caret-up"
                onClick={() => {
                  if (minutesLabel === '59') {
                    setMinutesLabel('00');
                  } else {
                    setMinutesLabel(
                      (parseInt(minutesLabel) + 1).toString().padStart(2, '0'),
                    );
                  }
                }}
              ></GoAIconButton>
              <input
                className={styles.timebox}
                type="text"
                value={minutesLabel}
                onChange={(e) => {
                  handleChange(e.target.value, setMinutesLabel, 59);
                }}
                onFocus={(e) => {
                  handleFocus(e.target.value, setMinutesLabel);
                }}
                onBlur={(e) => {
                  handleBlur(e.target.value, setMinutesLabel);
                }}
                tabIndex={3}
              ></input>
              <GoAIconButton
                icon="caret-down"
                onClick={() => {
                  if (minutesLabel === '00') {
                    setMinutesLabel('59');
                  } else {
                    setMinutesLabel(
                      (parseInt(minutesLabel) - 1).toString().padStart(2, '0'),
                    );
                  }
                }}
              ></GoAIconButton>
              <div className={styles.timecontainertext}>Minutes</div>
            </div>
            <div className={styles.timebuttonspacer}></div>
            <div className={styles.buttomcontainer}>
              <div className={styles.button} tabIndex={4}>
                <GoAButton
                  type={amSelected ? 'primary' : 'secondary'}
                  size="compact"
                  onClick={() => {
                    setAmSelected(true);
                    setPmSelected(false);
                  }}
                >
                  AM
                </GoAButton>
              </div>
              <GoASpacer vSpacing={'xs'} />
              <div className={styles.button} tabIndex={5}>
                <GoAButton
                  type={pmSelected ? 'primary' : 'secondary'}
                  size="compact"
                  onClick={() => {
                    setAmSelected(false);
                    setPmSelected(true);
                  }}
                >
                  PM
                </GoAButton>
              </div>
              <div style={{ minHeight: '20px' }}></div>
            </div>
            <GoASpacer hSpacing="l" />
          </div>
          <GoASpacer vSpacing="m" />
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <GoASpacer hSpacing="l" />
            <div className={styles.timecontainertext}>
              Enter hour and minutes using the arrows or enter directly in the
              input fields and indicate AM or PM.
            </div>
            <GoASpacer hSpacing="l"></GoASpacer>
          </div>
          <GoASpacer vSpacing="m" />
        </div>
      </div>
    </div>
  );
};
